import { useState, useMemo } from "react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { CalendarIcon, AlertCircle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useGroups } from "@/hooks/useGroups";
import { calculateGroupDates, formatSwedishDate } from "@/utils/groupScheduleUtils";
import type { Group } from "@/types/groups";
import { cn } from "@/lib/utils";

interface GroupSchedulerProps {
  group: Group;
  onClose?: () => void;
}

export const GroupScheduler = ({ group, onClose }: GroupSchedulerProps) => {
  const { updateGroup, acceptSchedule } = useGroups();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    group.first_possible_date ? new Date(group.first_possible_date) : undefined
  );
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const isScheduleAccepted = !!group.schedule_accepted_at;
  
  const dates = useMemo(() => {
    if (!selectedDate) return null;
    return calculateGroupDates(selectedDate);
  }, [selectedDate]);

  const handleDateSelect = async (date: Date | undefined) => {
    if (!date || isScheduleAccepted) return;
    
    setSelectedDate(date);
    
    await updateGroup.mutateAsync({
      id: group.id,
      first_possible_date: format(date, "yyyy-MM-dd"),
    });
  };

  const handleAcceptSchedule = async () => {
    await acceptSchedule.mutateAsync(group.id);
    setShowConfirmDialog(false);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            Schema för {group.name || `Grupp ${group.id.slice(0, 8)}`}
          </CardTitle>
          <CardDescription>
            {isScheduleAccepted 
              ? "Schemat är accepterat och gruppen har randomiserats"
              : "Ange första möjliga datum för att se möjliga scheman"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Date picker */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Första möjliga datum</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !selectedDate && "text-muted-foreground"
                  )}
                  disabled={isScheduleAccepted}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? formatSwedishDate(selectedDate) : "Välj datum"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  locale={sv}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Schedule preview */}
          {dates && !isScheduleAccepted && (
            <div className="grid md:grid-cols-2 gap-4">
              {/* Intervention scenario */}
              <div className="p-4 border rounded-lg bg-green-50 dark:bg-green-950/20">
                <h4 className="font-semibold text-green-800 dark:text-green-200 mb-3">
                  Om Interventionsgrupp
                </h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between">
                    <span className="text-muted-foreground">Första FÖS-träff:</span>
                    <span className="font-medium">{formatSwedishDate(dates.interventionStart)}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-muted-foreground">Sista FÖS-träff:</span>
                    <span className="font-medium">{formatSwedishDate(dates.interventionEnd)}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-muted-foreground">Uppföljnings-SMS:</span>
                    <span className="font-medium">{formatSwedishDate(dates.followUpSms)}</span>
                  </li>
                </ul>
              </div>

              {/* Control scenario */}
              <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950/20">
                <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-3">
                  Om Kontrollgrupp
                </h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between">
                    <span className="text-muted-foreground">Tematräff:</span>
                    <span className="font-medium">{formatSwedishDate(dates.themeStart)}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-muted-foreground">Första FÖS-träff:</span>
                    <span className="font-medium">{formatSwedishDate(dates.controlInterventionStart)}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-muted-foreground">Sista FÖS-träff:</span>
                    <span className="font-medium">{formatSwedishDate(dates.controlInterventionEnd)}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-muted-foreground">Uppföljnings-SMS:</span>
                    <span className="font-medium">{formatSwedishDate(dates.controlFollowUpSms)}</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {isScheduleAccepted && dates && (
            <Alert className={group.assigned_group === "intervention" ? "border-green-500" : "border-blue-500"}>
              <Check className="h-4 w-4" />
              <AlertTitle>
                {group.assigned_group === "intervention" ? "Interventionsgrupp" : "Kontrollgrupp"}
              </AlertTitle>
              <AlertDescription>
                <ul className="mt-2 space-y-1">
                  {group.assigned_group === "intervention" ? (
                    <>
                      <li>Första FÖS-träff: <strong>{formatSwedishDate(dates.interventionStart)}</strong></li>
                      <li>Sista FÖS-träff: <strong>{formatSwedishDate(dates.interventionEnd)}</strong></li>
                      <li>Uppföljnings-SMS: <strong>{formatSwedishDate(dates.followUpSms)}</strong></li>
                    </>
                  ) : (
                    <>
                      <li>Tematräff: <strong>{formatSwedishDate(dates.themeStart)}</strong></li>
                      <li>Första FÖS-träff: <strong>{formatSwedishDate(dates.controlInterventionStart)}</strong></li>
                      <li>Sista FÖS-träff: <strong>{formatSwedishDate(dates.controlInterventionEnd)}</strong></li>
                      <li>Uppföljnings-SMS: <strong>{formatSwedishDate(dates.controlFollowUpSms)}</strong></li>
                    </>
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {selectedDate && !isScheduleAccepted && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Viktigt</AlertTitle>
              <AlertDescription>
                När du accepterar schemat kommer gruppen att randomiseras och du kommer tilldelas ett av ovanstående scheman. Detta kan inte ångras. Säkerställ därför att båda varianterna som syns ovan fungerar för dig.
              </AlertDescription>
            </Alert>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 justify-end">
            {onClose && (
              <Button variant="outline" onClick={onClose}>
                Stäng
              </Button>
            )}
            {selectedDate && !isScheduleAccepted && (
              <Button 
                onClick={() => setShowConfirmDialog(true)}
                disabled={acceptSchedule.isPending}
              >
                {acceptSchedule.isPending ? "Accepterar..." : "Acceptera schema"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bekräfta schema</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill acceptera detta schema? 
              Din grupp kommer att randomiseras till antingen intervention eller kontroll. 
              Detta kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleAcceptSchedule}>
              Ja, acceptera schema
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
